/**
 * /onboarding/org — B2B 4-step setup wizard (G5).
 *
 *   Step 1 — Pick a plan          (deep-link to /subscriptions for now;
 *                                  mark complete on return via query)
 *   Step 2 — Verify org           (upload GST cert / Federation letter)
 *   Step 3 — Invite teammates     (email + role)
 *   Step 4 — Request archive data (optional, with note)
 *
 * Progress persists server-side (OrganizationOnboardingProgress).
 * Email-verify banner stays at the top until verified. Footer has a
 * 'Skip to dashboard' so the wizard is never a hard gate.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, Mail, ArrowRight, ArrowLeft, AlertTriangle,
  Upload, UserPlus, Archive, FileText, X,
} from "lucide-react";
import useAuthStore from "../../store/auth.store";
import useSubscriptionStore from "../../store/subscription.store";
import { resendVerification } from "../../api/auth.api";
import {
  getOnboardingProgress, updateOnboardingProgress,
  listVerificationDocs, uploadVerificationDoc,
  inviteTeammate, requestArchiveAccess,
} from "../../api/onboarding.api";

const STEPS = [
  { n: 1, label: "Pick a plan" },
  { n: 2, label: "Verify your org" },
  { n: 3, label: "Invite teammates" },
  { n: 4, label: "Request archive data" },
];

export default function OrgOnboarding() {
  const { user, isAuthenticated, loading: authLoading } = useAuthStore();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate("/login", { replace: true }); return; }
    if (!user?.organization_id) {
      // B2C user landed here by mistake — bounce to dashboard.
      navigate("/dashboard", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user?.organization_id]);

  // If user comes back from /subscriptions with ?from=plan, mark step 1 done.
  useEffect(() => {
    if (!progress) return;
    if (searchParams.get("from") === "plan" && !isComplete(1)) {
      bumpComplete(1, 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress]);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await getOnboardingProgress();
      setProgress(data);
    } finally {
      setLoading(false);
    }
  }

  function isComplete(step) {
    return (progress?.completed_steps || []).includes(step);
  }

  async function bumpComplete(stepDone, nextStep) {
    const completed = Array.from(
      new Set([...(progress?.completed_steps || []), stepDone]),
    );
    const { data } = await updateOnboardingProgress({
      completed_steps: completed,
      current_step: nextStep || Math.min(stepDone + 1, 4),
    });
    setProgress(data);
  }

  async function goToStep(step) {
    const { data } = await updateOnboardingProgress({ current_step: step });
    setProgress(data);
  }

  if (authLoading || loading || !progress) {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-secondary text-sm">
        Loading…
      </div>
    );
  }

  const currentStep = progress.current_step || 1;

  return (
    <div className="min-h-screen bg-bg-main py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <Header orgName={progress.org_name} />
        {!user?.email_verified && <VerifyEmailBanner email={user?.email} />}

        {/* Step indicator */}
        <StepStrip steps={STEPS} current={currentStep} completed={progress.completed_steps || []} onJump={goToStep} />

        {/* Active step */}
        <div className="rounded-xl border border-border-default bg-bg-primary p-5">
          {currentStep === 1 && (
            <Step1Plan progress={progress} onDone={() => bumpComplete(1, 2)} />
          )}
          {currentStep === 2 && (
            <Step2Verify progress={progress} onDone={() => bumpComplete(2, 3)} />
          )}
          {currentStep === 3 && (
            <Step3Invite progress={progress} onDone={() => bumpComplete(3, 4)} />
          )}
          {currentStep === 4 && (
            <Step4Archive progress={progress} onDone={() => bumpComplete(4)} />
          )}
        </div>

        {/* Sticky escape hatch */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => goToStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="text-sm text-text-secondary hover:text-text-primary disabled:opacity-40 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <Link to="/dashboard" className="text-sm text-text-secondary hover:text-text-primary">
            Skip — go to dashboard →
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Bits ──────────────────────────────────────────────────────────────────

function Header({ orgName }) {
  return (
    <header className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-bg-primary to-bg-primary p-6">
      <div className="text-xs uppercase tracking-widest text-emerald-300 mb-1">
        ZenCricketData · Owner setup
      </div>
      <h1 className="text-2xl md:text-3xl font-bold text-text-primary">
        Welcome aboard, {orgName}
      </h1>
      <p className="text-text-secondary text-sm mt-2">
        Four quick steps. Pick a plan, verify your org, bring your team, and
        unlock the archive. You can pause anywhere — we save your progress.
      </p>
    </header>
  );
}

function VerifyEmailBanner({ email }) {
  const [status, setStatus] = useState("idle");
  async function resend() {
    if (!email || status === "sending" || status === "sent") return;
    setStatus("sending");
    try { await resendVerification(email); setStatus("sent"); }
    catch { setStatus("err"); }
  }
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3">
      <Mail className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-amber-200">Verify your email</div>
        <div className="text-xs text-amber-100/80 mt-0.5">
          Sent to <span className="font-mono">{email || "your inbox"}</span>.
          Click the link to unlock plan purchases + paid features.
        </div>
      </div>
      {status === "sent" ? (
        <span className="text-xs text-emerald-300 flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" /> Sent
        </span>
      ) : (
        <button onClick={resend} disabled={status === "sending"}
          className="text-xs px-3 py-1.5 rounded-md border border-amber-400/60 text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">
          {status === "sending" ? "Sending…" : "Resend"}
        </button>
      )}
    </div>
  );
}

function StepStrip({ steps, current, completed, onJump }) {
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      {steps.map((s, i) => {
        const done = completed.includes(s.n);
        const active = s.n === current;
        return (
          <button
            key={s.n}
            onClick={() => onJump(s.n)}
            className="flex items-center gap-1.5"
          >
            <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${
              done ? "bg-emerald-500/30 text-emerald-200"
                   : active ? "bg-button-primary text-white"
                   : "bg-bg-secondary text-text-secondary"
            }`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.n}
            </span>
            <span className={active ? "font-semibold text-text-primary" : "text-text-secondary"}>
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span className="text-text-secondary mx-1">→</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Step 1 — Pick a plan ──────────────────────────────────────────────

function Step1Plan({ progress, onDone }) {
  // We don't reproduce the full subscription picker here — kick the user
  // out to /subscriptions which has the canonical UI, and capture
  // ?from=plan on return to mark this step done.
  const { mySubscription, fetchMySubscription } = useSubscriptionStore();
  useEffect(() => { fetchMySubscription(); }, [fetchMySubscription]);
  const hasActive = mySubscription?.status === "active";

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <span className="text-emerald-300">1.</span> Pick a plan
      </h2>
      <p className="text-sm text-text-secondary">
        Your plan unlocks seats, data quotas, and feature gates. Basic is fine
        for getting started; upgrade later from the Subscriptions page.
      </p>
      {hasActive ? (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
          <div className="text-emerald-200 font-semibold">
            ✓ {mySubscription.plan?.name || "Plan"} active
          </div>
          <div className="text-xs text-emerald-100/80 mt-0.5">
            Billing cycle {mySubscription.plan?.billing_cycle || "—"}.
          </div>
        </div>
      ) : (
        <Link
          to="/subscriptions?return=onboarding"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
        >
          Pick a plan <ArrowRight className="w-4 h-4" />
        </Link>
      )}
      <button
        onClick={onDone}
        className="block text-xs text-text-secondary hover:text-text-primary mt-2"
      >
        {hasActive ? "Continue to step 2 →" : "Skip this step for now →"}
      </button>
    </div>
  );
}

// ─── Step 2 — Verify org (doc upload) ──────────────────────────────────

const DOC_TYPES = [
  { key: "GST_CERT",          label: "GST certificate" },
  { key: "FEDERATION_LETTER", label: "Federation / Association letter" },
  { key: "PAN",               label: "PAN card" },
  { key: "INCORPORATION",     label: "Incorporation certificate" },
  { key: "OTHER",             label: "Other supporting document" },
];

function Step2Verify({ progress, onDone }) {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("GST_CERT");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { reload(); }, []);
  async function reload() {
    try {
      const { data } = await listVerificationDocs();
      setDocs(data);
    } catch (e) { /* ignore — list is informational */ }
  }

  async function onUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(null);
    try {
      await uploadVerificationDoc(file, docType);
      await reload();
      e.target.value = "";
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <span className="text-emerald-300">2.</span> Verify your org
      </h2>
      <p className="text-sm text-text-secondary">
        Upload one or more supporting docs so we can mark your org Verified.
        Verification unlocks pro-match features. PDF or photo, ≤ 8MB each.
      </p>

      <div className="flex flex-wrap gap-2 items-center">
        <select value={docType} onChange={(e) => setDocType(e.target.value)}
          className="text-sm rounded-md bg-input border border-border-default px-2 py-2">
          {DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
        <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-md bg-button-primary text-white font-semibold cursor-pointer text-sm ${busy ? "opacity-50 cursor-not-allowed" : ""}`}>
          <Upload className="w-4 h-4" />
          {busy ? "Uploading…" : "Upload doc"}
          <input type="file" disabled={busy} onChange={onUpload}
            accept="application/pdf,image/jpeg,image/png,image/heic"
            className="hidden" />
        </label>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-2 text-xs">
          {error}
        </div>
      )}

      {/* Uploaded list */}
      <div className="space-y-1">
        {docs.length === 0 && (
          <div className="text-xs text-text-secondary">No documents yet.</div>
        )}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-xs px-3 py-2 rounded bg-bg-secondary/40">
            <FileText className="w-3.5 h-3.5 text-text-secondary shrink-0" />
            <span className="font-mono truncate flex-1">
              {d.original_filename || d.doc_type}
            </span>
            <StatusPill status={d.status} />
          </div>
        ))}
      </div>

      <button
        onClick={onDone}
        disabled={docs.length === 0}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50 mt-2"
      >
        Done — continue <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-[10px] text-text-secondary mt-1">
        Verification review is manual; usually 1 business day.
      </p>
    </div>
  );
}

function StatusPill({ status }) {
  const COLOR = {
    PENDING:  "bg-amber-500/20 text-amber-200 border-amber-500/40",
    APPROVED: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
    REJECTED: "bg-rose-500/20 text-rose-200 border-rose-500/40",
  };
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${COLOR[status] || ""}`}>
      {status}
    </span>
  );
}

// ─── Step 3 — Invite teammates ─────────────────────────────────────────

function Step3Invite({ progress, onDone }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("USER");
  const [sent, setSent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setError(null);
    try {
      const { data } = await inviteTeammate(email.trim(), role);
      setSent((s) => [data, ...s]);
      setEmail("");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <span className="text-emerald-300">3.</span> Invite teammates
      </h2>
      <p className="text-sm text-text-secondary">
        Send invites to anyone on your team who will score, manage, or view
        data. They'll get an email link to set a password.
      </p>

      <form onSubmit={submit} className="flex flex-wrap gap-2 items-center">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@example.com"
          className="flex-1 min-w-[200px] px-3 py-2 rounded-md bg-input border border-border-default text-sm"
        />
        <select value={role} onChange={(e) => setRole(e.target.value)}
          className="text-sm rounded-md bg-input border border-border-default px-2 py-2">
          <option value="USER">User</option>
          <option value="ADMIN">Admin</option>
          <option value="SUPER_ADMIN">Super Admin</option>
        </select>
        <button type="submit" disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-button-primary text-white text-sm font-semibold disabled:opacity-50">
          <UserPlus className="w-4 h-4" />
          {busy ? "Sending…" : "Send invite"}
        </button>
      </form>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-2 text-xs">
          {error}
        </div>
      )}

      {sent.length > 0 && (
        <div className="space-y-1 pt-2">
          <div className="text-xs uppercase tracking-widest text-text-secondary mb-1">
            Sent this session
          </div>
          {sent.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-bg-secondary/40">
              <Mail className="w-3.5 h-3.5 text-text-secondary" />
              <span className="font-mono truncate flex-1">{s.email}</span>
              <span className="text-text-secondary">{s.role}</span>
              {s.resent && <span className="text-amber-300 text-[10px] uppercase">resent</span>}
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onDone}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold mt-2"
      >
        Done — continue <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Step 4 — Archive data request ─────────────────────────────────────

function Step4Archive({ progress, onDone }) {
  const [requested, setRequested] = useState(progress.archive_requested);
  const [note, setNote] = useState(progress.archive_request_note || "");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function submit() {
    setBusy(true);
    try {
      await requestArchiveAccess(note);
      setRequested(true);
      onDone?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <span className="text-emerald-300">4.</span> Request archive access (optional)
      </h2>
      <p className="text-sm text-text-secondary">
        Optional. Tell us if you want access to the historical archive
        (3,236 fully-scored matches, ball-by-ball + analytics) or Federation
        data feeds. Our team reviews and grants access manually.
      </p>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={4}
        placeholder="Tell us what you need — e.g. 'IPL 2024+2025 matches for fan-engagement analytics' or 'Match ball data for Federation X for the 2026 season'."
        className="w-full px-3 py-2 rounded-md bg-input border border-border-default text-sm"
      />

      {requested && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
          <div className="text-emerald-200 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Request submitted
          </div>
          <div className="text-xs text-emerald-100/80 mt-0.5">
            We'll review and email you within 1-2 business days.
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!requested && (
          <button
            onClick={submit}
            disabled={busy || !note.trim()}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50"
          >
            <Archive className="w-4 h-4" />
            {busy ? "Submitting…" : "Submit request"}
          </button>
        )}
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          {requested ? "Finish — go to dashboard" : "Skip — go to dashboard"} →
        </button>
      </div>
    </div>
  );
}
